import * as XLSX from 'xlsx';
import { corrigirNotacaoCientifica } from './xlsxNotacaoCientifica';

/**
 * APARELHOS — parsing dos dois arquivos da Claro (canal AACE).
 *
 * TABELA PME (mensal): a aba "Tabela de Preços" é uma MATRIZ modelo × plano.
 * O mesmo aparelho tem 13 preços: base à vista (SEM subsídio), TOTAL
 * COMPARTILHADO (<6GB), "Híbrido" 10–150GB (colunas M–R; na tela chamamos de
 * VENDA NOVA — "híbrido" nao e' familia de plano da Claro, e' apelido que nasceu
 * aqui), Renovação 6–100GB e Total
 * Individual 200GB. A coluna CÓDIGO traz as variações de cor do modelo
 * separadas por ';' — o estoque cruza por esses códigos, nunca por nome.
 *
 * ESTOQUE ZT438 (diário): foto substitutiva do dia. Código com 18 dígitos e
 * zeros à esquerda; a MESMA descrição aparece com códigos diferentes.
 *
 * Regras oficiais (impressas na tabela):
 *  - piso R$ 48,00 por aparelho = 24× R$ 2,00 ou 12× R$ 4,00 (parcela na fatura)
 *  - à vista (cartão) = preço BASE, sem subsídio — NUNCA o preço da matriz
 *  - plano abaixo de 6GB usa a coluna TOTAL COMPARTILHADO
 */

export interface MatrizPrecosAparelho {
  base: number | null;
  totalCompartilhado: number | null;
  hibrido: Record<number, number>;
  renovacao: Record<number, number>;
  totalIndividual: Record<number, number>;
}

export interface ModeloPME {
  slug: string;
  nome: string;
  fabricante: string;
  gama: string;
  tecnologia: string;
  tipoSimcard: string;
  codigos: string[];
  precos: MatrizPrecosAparelho;
}

export interface TabelaPMEParseada {
  competencia: string;
  validaDesde: string | null;
  modelos: ModeloPME[];
  codigosCadastro: { codigo: string; modelo: string }[];
  avisos: string[];
}

export interface EstoqueLinha {
  codigo: string;
  descricao: string;
  quantidade: number;
  centro: string;
  deposito: string;
}

export interface EstoqueParseado {
  dataFoto: string;
  linhas: EstoqueLinha[];
  descartadas: number;
  avisos: string[];
}

const MESES: Record<string, string> = {
  JANEIRO: '01', FEVEREIRO: '02', MARCO: '03', MARÇO: '03', ABRIL: '04',
  MAIO: '05', JUNHO: '06', JULHO: '07', AGOSTO: '08', SETEMBRO: '09',
  OUTUBRO: '10', NOVEMBRO: '11', DEZEMBRO: '12',
};

export function slugAparelho(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function normalizarCodigoMaterial(bruto: unknown): string {
  return String(bruto ?? '').trim().replace(/^0+/, '');
}

/** Aparelho/equipamento vendável vs simcard/cartão (96% do arquivo de estoque). */
export function ehAparelho(descricao: string): boolean {
  return /^(AP |MODEM|ROTEADOR|TABLET|SMARTWATCH|NOTEBOOK)/i.test(descricao.trim());
}

export function ehDemo(descricao: string): boolean {
  return /\bDEMO\b/i.test(descricao);
}

function celula(ws: XLSX.WorkSheet, col: string, row: number): unknown {
  const c = ws[`${col}${row}`] as { v?: unknown } | undefined;
  return c?.v;
}

function celulaStr(ws: XLSX.WorkSheet, col: string, row: number): string {
  const v = celula(ws, col, row);
  return v == null ? '' : String(v).trim();
}

function celulaNum(ws: XLSX.WorkSheet, col: string, row: number): number | null {
  const v = celula(ws, col, row);
  if (v == null || v === '' || v === '-') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/\./g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function extrairCompetencia(texto: string): string | null {
  const m = texto.toUpperCase().match(/TABELA:\s*([A-ZÇ]+)[\s/]*(\d{4})/);
  if (!m) return null;
  const mes = MESES[m[1]];
  return mes ? `${m[2]}-${mes}` : null;
}

export function parseTabelaPME(data: ArrayBuffer): TabelaPMEParseada {
  const wb = XLSX.read(data, { type: 'array' });
  const avisos: string[] = [];

  const abaPrecos = wb.SheetNames.find((n) => /tabela de pre/i.test(n));
  if (!abaPrecos) throw new Error('Aba "Tabela de Preços" não encontrada — este é o arquivo TABELA PME?');
  const ws = wb.Sheets[abaPrecos];
  corrigirNotacaoCientifica(ws);

  const competencia = extrairCompetencia(celulaStr(ws, 'B', 3));
  if (!competencia) throw new Error(`Não achei a competência em B3 ("${celulaStr(ws, 'B', 3)}") — esperado "TABELA: JULHO 2026".`);
  const validaDesde = celulaStr(ws, 'B', 4).replace(/.*?:\s*/, '') || null;

  const HIBRIDO_GB = { M: 10, N: 15, O: 25, P: 50, Q: 80, R: 150 } as const;
  const RENOVACAO_GB = { S: 6, T: 20, U: 30, V: 40, W: 100 } as const;

  const modelos: ModeloPME[] = [];
  let vaziasSeguidas = 0;
  for (let r = 16; r <= 4000 && vaziasSeguidas < 20; r++) {
    const fabricante = celulaStr(ws, 'B', r);
    if (!fabricante) { vaziasSeguidas++; continue; }
    vaziasSeguidas = 0;
    const nome = celulaStr(ws, 'D', r);
    if (!nome) { avisos.push(`Linha ${r}: fabricante sem modelo — pulada`); continue; }

    const codigos = celulaStr(ws, 'C', r)
      .split(';')
      .map(normalizarCodigoMaterial)
      .filter(Boolean);
    if (!codigos.length) avisos.push(`${nome}: sem código de material (linha ${r})`);

    const hibrido: Record<number, number> = {};
    for (const [col, gb] of Object.entries(HIBRIDO_GB)) {
      const p = celulaNum(ws, col, r);
      if (p != null) hibrido[gb] = p;
    }
    const renovacao: Record<number, number> = {};
    for (const [col, gb] of Object.entries(RENOVACAO_GB)) {
      const p = celulaNum(ws, col, r);
      if (p != null) renovacao[gb] = p;
    }
    const totalIndividual: Record<number, number> = {};
    const p200 = celulaNum(ws, 'X', r);
    if (p200 != null) totalIndividual[200] = p200;

    modelos.push({
      slug: slugAparelho(nome),
      nome,
      fabricante,
      gama: celulaStr(ws, 'E', r) || 'SEM GAMA',
      tecnologia: celulaStr(ws, 'F', r),
      tipoSimcard: celulaStr(ws, 'G', r),
      codigos,
      precos: {
        base: celulaNum(ws, 'I', r),
        totalCompartilhado: celulaNum(ws, 'K', r),
        hibrido,
        renovacao,
        totalIndividual,
      },
    });
  }
  if (!modelos.length) throw new Error('Nenhum modelo lido da Tabela de Preços — layout mudou?');

  const abaInternet = wb.SheetNames.find((n) => /internet/i.test(n));
  if (abaInternet) {
    const wsi = wb.Sheets[abaInternet];
    corrigirNotacaoCientifica(wsi);
    let vazias = 0;
    for (let r = 11; r <= 200 && vazias < 10; r++) {
      const fabricante = celulaStr(wsi, 'B', r);
      if (!fabricante) { vazias++; continue; }
      vazias = 0;
      const nome = celulaStr(wsi, 'D', r);
      if (!nome) continue;
      const totalIndividual: Record<number, number> = {};
      const p200 = celulaNum(wsi, 'K', r);
      const p400 = celulaNum(wsi, 'L', r);
      if (p200 != null) totalIndividual[200] = p200;
      if (p400 != null) totalIndividual[400] = p400;
      modelos.push({
        slug: slugAparelho(nome),
        nome,
        fabricante,
        gama: celulaStr(wsi, 'E', r).toUpperCase() || 'INTERNET',
        tecnologia: celulaStr(wsi, 'F', r),
        tipoSimcard: celulaStr(wsi, 'G', r),
        codigos: celulaStr(wsi, 'C', r).split(';').map(normalizarCodigoMaterial).filter(Boolean),
        precos: { base: celulaNum(wsi, 'I', r), totalCompartilhado: null, hibrido: {}, renovacao: {}, totalIndividual },
      });
    }
  } else {
    avisos.push('Aba INTERNET MÓVEL não encontrada — modems/roteadores ficaram de fora');
  }

  const codigosCadastro: { codigo: string; modelo: string }[] = [];
  const abaCadastro = wb.SheetNames.find((n) => /cadastro/i.test(n));
  if (abaCadastro) {
    const wsc = wb.Sheets[abaCadastro];
    corrigirNotacaoCientifica(wsc);
    let vazias = 0;
    for (let r = 7; r <= 5000 && vazias < 30; r++) {
      const codigo = normalizarCodigoMaterial(celula(wsc, 'A', r));
      if (!codigo) { vazias++; continue; }
      vazias = 0;
      const modelo = celulaStr(wsc, 'B', r);
      if (modelo) codigosCadastro.push({ codigo, modelo });
    }
  }

  return { competencia, validaDesde, modelos, codigosCadastro, avisos };
}

export function parseEstoqueZT438(data: ArrayBuffer): EstoqueParseado {
  const wb = XLSX.read(data, { type: 'array' });
  const avisos: string[] = [];

  const aba = wb.SheetNames.find((n) => /^ZT438/i.test(n));
  if (!aba) throw new Error('Aba "ZT438_EstoqueApSimcard_*" não encontrada — este é o arquivo de estoque?');

  const mData = aba.match(/(\d{2})(\d{2})(\d{4})\s*$/);
  if (!mData) throw new Error(`Não achei a data no nome da aba ("${aba}") — esperado ZT438_EstoqueApSimcard_DDMMAAAA.`);
  const dataFoto = `${mData[3]}-${mData[2]}-${mData[1]}`;

  const ws = wb.Sheets[aba];
  corrigirNotacaoCientifica(ws);
  const linhasBrutas = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: false });

  const header = (linhasBrutas[0] ?? []).map((c) => String(c ?? '').trim().toUpperCase());
  if (header[2] !== 'PRODUTO' || !/^ESTOQUE/.test(header[4] ?? '')) {
    throw new Error(`Cabeçalho inesperado (${header.slice(0, 5).join(' | ')}) — esperado CENTRO/DEPOSITO/PRODUTO/DESCRIÇÃO/ESTOQUE.`);
  }

  const linhas: EstoqueLinha[] = [];
  let descartadas = 0;
  for (const raw of linhasBrutas.slice(1)) {
    const codigo = normalizarCodigoMaterial(raw?.[2]);
    if (!codigo) { if (raw?.some((c) => String(c ?? '').trim())) descartadas++; continue; }
    const quantidade = parseInt(String(raw[4] ?? '0').replace(/\D/g, ''), 10) || 0;
    linhas.push({
      codigo,
      descricao: String(raw[3] ?? '').trim(),
      quantidade,
      centro: String(raw[0] ?? '').trim(),
      deposito: String(raw[1] ?? '').trim(),
    });
  }
  if (!linhas.length) throw new Error('Nenhuma linha de estoque lida — arquivo vazio?');
  if (descartadas) avisos.push(`${descartadas} linha(s) sem código de material descartada(s)`);

  return { dataFoto, linhas, descartadas, avisos };
}

// ---------------------------------------------------------------------------
// Resolução de preço na proposta — o coração da regra comercial.
// ---------------------------------------------------------------------------

export type GrupoPlanoAparelho = 'RENOVACAO' | 'HIBRIDO' | 'TOTAL_INDIVIDUAL' | 'TOTAL_COMPARTILHADO';
export type FormaPagamentoAparelho = '24x' | '12x' | 'avista';

export const PISO_APARELHO_RS = 48;

// ---------------------------------------------------------------------------
// Tier do BÔNUS APARELHO — classificação por FAIXA DE PREÇO BASE.
// ---------------------------------------------------------------------------

export type TierAparelho = 'baixo' | 'medio' | 'alto';

/**
 * Corte provisório definido em 09/08/2026 sobre a tabela de agosto (134 modelos).
 * Os dois limites caem em lacunas reais da tabela — não há modelo entre
 * R$ 1.499 e R$ 1.599, nem entre R$ 3.099 e R$ 3.599 — então a régua não
 * separa aparelhos de preço vizinho em tiers diferentes.
 * Substituir por `tier_origem: 'manual'` (ou pela régua nova) quando o anexo
 * oficial modelo→Tier da campanha 167/2026 chegar.
 */
export const FAIXAS_TIER_APARELHO = { medio: 1500, alto: 3500 } as const;

export function tierPorFaixa(precoBase: number | null | undefined): TierAparelho | null {
  if (precoBase == null || precoBase <= 0) return null;
  if (precoBase < FAIXAS_TIER_APARELHO.medio) return 'baixo';
  if (precoBase < FAIXAS_TIER_APARELHO.alto) return 'medio';
  return 'alto';
}

export interface PrecoResolvido {
  preco: number;
  grupoUsado: GrupoPlanoAparelho | 'BASE';
  faixaGb: number | null;
  subsidiado: boolean;
}

/**
 * Resolve o preço do aparelho para o plano da linha.
 *  - à vista → SEMPRE o preço base (sem subsídio) — regra da Claro
 *  - gb < 6 → coluna TOTAL COMPARTILHADO
 *  - senão → no grupo do plano, a MAIOR faixa de GB que seja ≤ ao gb do plano
 *    (conservador: nunca dá subsídio de faixa que o cliente não alcançou);
 *    sem faixa alcançada, cai para TOTAL COMPARTILHADO, depois base.
 */
export function resolverPrecoAparelho(
  m: MatrizPrecosAparelho,
  grupo: GrupoPlanoAparelho,
  gb: number | null,
  forma: FormaPagamentoAparelho,
): PrecoResolvido | null {
  if (forma === 'avista') {
    if (m.base == null) return null;
    return { preco: m.base, grupoUsado: 'BASE', faixaGb: null, subsidiado: false };
  }

  if ((gb != null && gb < 6) || grupo === 'TOTAL_COMPARTILHADO') {
    if (m.totalCompartilhado != null) {
      return { preco: m.totalCompartilhado, grupoUsado: 'TOTAL_COMPARTILHADO', faixaGb: null, subsidiado: true };
    }
  }

  const tabela = grupo === 'RENOVACAO' ? m.renovacao : grupo === 'TOTAL_INDIVIDUAL' ? m.totalIndividual : m.hibrido;
  const faixas = Object.keys(tabela).map(Number).sort((a, b) => a - b);
  const alcancadas = gb == null ? faixas : faixas.filter((f) => f <= gb);
  if (alcancadas.length) {
    const faixa = alcancadas[alcancadas.length - 1];
    return { preco: tabela[faixa], grupoUsado: grupo, faixaGb: faixa, subsidiado: true };
  }

  if (m.totalCompartilhado != null) {
    return { preco: m.totalCompartilhado, grupoUsado: 'TOTAL_COMPARTILHADO', faixaGb: null, subsidiado: true };
  }
  if (m.base != null) return { preco: m.base, grupoUsado: 'BASE', faixaGb: null, subsidiado: false };
  return null;
}

export function parcelasAparelho(preco: number, forma: FormaPagamentoAparelho): { n: number; valor: number } | null {
  if (forma === 'avista') return null;
  const n = forma === '24x' ? 24 : 12;
  return { n, valor: Math.round((preco / n) * 100) / 100 };
}
